import { CommonModule } from "@angular/common";
import { Component, computed, inject, input, signal } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { QuizService } from "src/app/services/api/quiz.service";
import { Quiz, QuizAttempt } from "src/app/services/model/quiz.model";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { SharedModule } from "src/shared/shared.module";

/**
 * The quizzes attached to one course, shown inside the watch screen.
 *
 * This is where the legacy exam accordion used to sit. The difference is that
 * these are drawn from the shared question bank per attempt, so "practise this
 * chapter again" is a genuinely different paper rather than the same one
 * re-read.
 */
@Component({
  selector: "app-course-quiz-list",
  imports: [SharedModule, CommonModule, RouterModule, TranslateModule],
  templateUrl: "./course-quiz-list.component.html",
})
export class CourseQuizListComponent {
  readonly courseId = input.required<number>();

  #quizService = inject(QuizService);
  #toastService = inject(ToastService);
  #router = inject(Router);

  isLoading = signal(true);
  quizzes = signal<Quiz[]>([]);
  attempts = signal<QuizAttempt[]>([]);

  bestByQuiz = computed(() => {
    const best = new Map<number, QuizAttempt>();
    for (const attempt of this.attempts()) {
      if (attempt.status !== "submitted") continue;
      const current = best.get(attempt.quiz_id);
      if (!current || attempt.percent > current.percent) best.set(attempt.quiz_id, attempt);
    }
    return best;
  });

  ngOnInit(): void {
    this.#quizService.getQuizzes({ course_id: this.courseId() }).subscribe({
      next: (res: any) => {
        this.quizzes.set(res?.data ?? []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });

    this.#quizService.getAttempts({ limit: 50 }).subscribe({
      next: (res: any) => this.attempts.set(res?.data ?? []),
      error: () => this.attempts.set([]),
    });
  }

  best(quiz: Quiz): QuizAttempt | undefined {
    return this.bestByQuiz().get(quiz.id);
  }

  start(quiz: Quiz): void {
    this.#quizService.startAttempt(quiz.id).subscribe({
      next: () => this.#router.navigate(["/online-class/quiz", quiz.id]),
      error: (err) =>
        this.#toastService.toastError(err?.error?.errors || "เริ่มทำแบบฝึกหัดไม่สำเร็จ"),
    });
  }
}
